import { useCallback, useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { AdminInsights } from "@/components/admin/Insights";
import { FeedbackQueue } from "@/components/admin/FeedbackQueue";
import { Overview, DownloadsTab, ImpactTab } from "@/components/admin/Metrics";
import { Roadmap } from "@/components/admin/Roadmap";
import { ResourcesAdmin } from "@/components/admin/ResourcesAdmin";
import { Reports } from "@/components/admin/Reports";
import { Messages } from "@/components/admin/Messages";

const TABS = [["overview", "Overview"], ["downloads", "Downloads & visitors"], ["messages", "Messages"], ["feedback", "Feedback"], ["impact", "Impact"], ["roadmap", "Roadmap"],
  ["resources", "Resources & versions"], ["reports", "Reports"], ["security", "Security"]];
const arr = (r) => (r.status === "fulfilled" && Array.isArray(r.value?.data) ? r.value.data : []);

const useAdminData = () => {
  const [d, setD] = useState({ m: null, fb: [], imp: [], rm: [], res: [], v: null, msg: [] });
  const load = useCallback(async () => {
    const [m, fb, imp, rm, res, v, msg] = await Promise.allSettled(["/admin/metrics", "/feedback", "/admin/impact", "/admin/roadmap", "/admin/resources", "/admin/visits", "/admin/messages"].map((u) => api.get(u)));
    setD({ m: m.status === "fulfilled" ? m.value.data : null, fb: arr(fb), imp: arr(imp), rm: arr(rm), res: arr(res), v: v.status === "fulfilled" ? v.value.data : null, msg: arr(msg) });
  }, []);
  useEffect(() => { load(); }, [load]);
  return [d, setD, load];
};

export const AdminDashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [d, setD, load] = useAdminData();
  const setFb = (fn) => setD((s) => ({ ...s, fb: fn(s.fb) }));
  const setMsg = (fn) => setD((s) => ({ ...s, msg: fn(s.msg) }));
  const panel = {
    overview: <Overview m={d.m} v={d.v} newMessages={d.msg.filter((x) => x.status === "NEW").length} />,
    downloads: <DownloadsTab m={d.m} v={d.v} />,
    messages: <Messages items={d.msg} setItems={setMsg} />,
    feedback: <FeedbackQueue items={d.fb} setItems={setFb} />,
    impact: <ImpactTab m={d.m} items={d.imp} />,
    roadmap: <Roadmap items={d.rm} reload={load} />,
    resources: <ResourcesAdmin items={d.res} reload={load} />,
    reports: <Reports data={d} />,
    security: <AdminInsights />,
  }[tab];
  return (
    <div className="no-print" data-testid="admin-inbox">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Signed in as {user.email}</p>
          <h1 className="mt-2 font-display text-4xl font-medium sm:text-5xl">Admin dashboard</h1>
        </div>
        <button type="button" onClick={onLogout} className="btn btn-ghost btn-sm" data-testid="admin-logout"><LogOut size={14} aria-hidden /> Sign out</button>
      </div>
      <div role="tablist" aria-label="Admin sections" className="mt-8 flex gap-2 overflow-x-auto pb-2">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => { setTab(id); load(); }}
            className={`chip shrink-0 ${tab === id ? "bg-brand text-brand-fg" : ""}`} data-testid={`admin-tab-${id}`}>{label}</button>
        ))}
      </div>
      <div className="mt-6" role="tabpanel">{panel}</div>
    </div>
  );
};
